import React, { useContext, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import {
  AmountOverrideDto,
  AmountOverridePayload,
  DisableOverrideDto,
  DisableOverridePayload,
} from '@/api/Wallet/Types/WithdrawalAutomationDto'
import AddButton from '@/components/AddButton/AddButton'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal'
import { Loading } from '@/components/Loading'
import MoreButton from '@/components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Body1 } from '@/components/ui/typography'
import { useApiClient } from '@/utils/api/ApiClient'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from '@/utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { AuthSessionContext } from '@/utils/AuthContext'
import { links } from '@/utils/featureToggles'
import { hasPermission } from '@/utils/permissionsValidator'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'

import { AmountOverrideForm } from './AmountOverrideForm'
import amountOverridesColumns from './AmountOverridesColumns'
import { DisableOverrideForm } from './DisableOverrideForm'
import disableOverridesColumns from './DisableOverridesColumns'
import {
  MLWithdrawalAutomationNavigation,
  MLWithdrawalAutomationTab,
} from './MLWithdrawalAutomationNavigation'
import { EMPTY_SCOPE } from './overrideScope'
import { ScopeOptions } from './OverrideScopeFields'
import { useWithdrawalAutomationOptions } from './useWithdrawalAutomationOptions'

interface DisableModalState {
  title: string
  id?: string
  initialValues: DisableOverridePayload
}

interface AmountModalState {
  title: string
  id?: string
  initialValues: AmountOverridePayload
}

interface DeleteModalState {
  type: 'disable' | 'amount'
  id: string
}

export const MLWithdrawalAutomationOverridesPage: React.FC = () => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const canEdit = hasPermission(permissions, PermissionsEnum.CanEditMLWithdrawalAutomationSettings)

  const {
    companyOptions,
    countryOptions,
    paymentProviderOptions,
    clientTypeOptions,
    walletTypeOptions,
    currencyOptions,
  } = useWithdrawalAutomationOptions()

  const scopeOptions: ScopeOptions = {
    companyOptions,
    countryOptions,
    paymentProviderOptions,
    clientTypeOptions,
    walletTypeOptions,
  }

  const { callback: disableCallback, forceRefresh: refreshDisable } = useCallbackWithForceRefresh(
    () => walletsApiClient.getWithdrawalAutomationDisableOverrides(),
    []
  )
  const { data: disableOverrides, isLoading: isLoadingDisable } =
    useFetchOneWithFetchingUpdate(disableCallback)

  const { callback: amountCallback, forceRefresh: refreshAmount } = useCallbackWithForceRefresh(
    () => walletsApiClient.getWithdrawalAutomationAmountOverrides(),
    []
  )
  const { data: amountOverrides, isLoading: isLoadingAmount } =
    useFetchOneWithFetchingUpdate(amountCallback)

  const [disableModal, setDisableModal] = useState<DisableModalState | null>(null)
  const [amountModal, setAmountModal] = useState<AmountModalState | null>(null)
  const [deleteModal, setDeleteModal] = useState<DeleteModalState | null>(null)

  const reportError = (error: unknown) => setToast(errorToast((error as Error).message))

  const openCreateDisable = () =>
    setDisableModal({
      title: 'Create Disable Override',
      initialValues: { ...EMPTY_SCOPE, enabled: true },
    })

  const openEditDisable = (row: DisableOverrideDto) =>
    setDisableModal({
      title: 'Edit Disable Override',
      id: row.id,
      initialValues: {
        tickmillCompanyId: row.tickmillCompanyId,
        countryId: row.countryId,
        paymentProviderId: row.paymentProviderId,
        clientTypeId: row.clientTypeId,
        walletTypeId: row.walletTypeId,
        enabled: row.enabled,
      },
    })

  const openCreateAmount = () =>
    setAmountModal({
      title: 'Create Amount Override',
      initialValues: { ...EMPTY_SCOPE, enabled: true, limits: [] },
    })

  const openEditAmount = (row: AmountOverrideDto) =>
    setAmountModal({
      title: 'Edit Amount Override',
      id: row.id,
      initialValues: {
        tickmillCompanyId: row.tickmillCompanyId,
        countryId: row.countryId,
        paymentProviderId: row.paymentProviderId,
        clientTypeId: row.clientTypeId,
        walletTypeId: row.walletTypeId,
        enabled: row.enabled,
        limits: row.limits.map((limit) => ({
          currencyId: limit.currencyId,
          maxAmount: limit.maxAmount,
        })),
      },
    })

  const handleSubmitDisable = async (values: DisableOverridePayload) => {
    try {
      if (disableModal?.id) {
        await walletsApiClient.updateWithdrawalAutomationDisableOverride(disableModal.id, values)
        setToast(successToast('Disable override updated successfully.'))
      } else {
        await walletsApiClient.createWithdrawalAutomationDisableOverride(values)
        setToast(successToast('Disable override created successfully.'))
      }
      setDisableModal(null)
      refreshDisable()
    } catch (error) {
      reportError(error)
    }
  }

  const handleSubmitAmount = async (values: AmountOverridePayload) => {
    try {
      if (amountModal?.id) {
        await walletsApiClient.updateWithdrawalAutomationAmountOverride(amountModal.id, values)
        setToast(successToast('Amount override updated successfully.'))
      } else {
        await walletsApiClient.createWithdrawalAutomationAmountOverride(values)
        setToast(successToast('Amount override created successfully.'))
      }
      setAmountModal(null)
      refreshAmount()
    } catch (error) {
      reportError(error)
    }
  }

  const handleToggleDisable = async (row: DisableOverrideDto) => {
    try {
      await walletsApiClient.setWithdrawalAutomationDisableOverrideEnabled(row.id, {
        enabled: !row.enabled,
      })
      setToast(successToast('Disable override updated successfully.'))
      refreshDisable()
    } catch (error) {
      reportError(error)
    }
  }

  const handleToggleAmount = async (row: AmountOverrideDto) => {
    try {
      await walletsApiClient.setWithdrawalAutomationAmountOverrideEnabled(row.id, {
        enabled: !row.enabled,
      })
      setToast(successToast('Amount override updated successfully.'))
      refreshAmount()
    } catch (error) {
      reportError(error)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteModal) return
    try {
      if (deleteModal.type === 'disable') {
        await walletsApiClient.deleteWithdrawalAutomationDisableOverride(deleteModal.id)
        refreshDisable()
      } else {
        await walletsApiClient.deleteWithdrawalAutomationAmountOverride(deleteModal.id)
        refreshAmount()
      }
      setToast(successToast('Override deleted successfully.'))
      setDeleteModal(null)
    } catch (error) {
      reportError(error)
    }
  }

  const renderRowActions = (
    onEdit: () => void,
    onToggle: () => void,
    onDelete: () => void,
    enabled: boolean
  ) => (
    <td>
      <MoreButton
        items={[
          { name: 'Edit', disabled: !canEdit, onClick: onEdit },
          { name: enabled ? 'Disable' : 'Enable', disabled: !canEdit, onClick: onToggle },
          { name: 'Delete', disabled: !canEdit, onClick: onDelete },
        ]}
      />
    </td>
  )

  if (!hasPermission(permissions, PermissionsEnum.CanViewMLWithdrawalAutomationSettings)) {
    return null
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Withdrawal Automation (ML)',
            link: links.administrationMlWithdrawalAutomationOverrides.url,
          },
        ]}
      />
      <MLWithdrawalAutomationNavigation tab={MLWithdrawalAutomationTab.OVERRIDES} />

      <div className='flex flex-col gap-8'>
        <section>
          <div className='flex items-center justify-between'>
            <Body1 bold>Disable Overrides</Body1>
            <AddButton
              text='Create Disable Override'
              disabled={!canEdit}
              onClick={openCreateDisable}
            />
          </div>
          <Loading isLoading={isLoadingDisable} fullWidth={false}>
            <Grid data={disableOverrides ?? []}>
              {disableOverridesColumns
                .filter((column) => column.show !== false)
                .map((column, index) => (
                  <Column key={index} {...column} />
                ))}
              <Column
                title=''
                filterable={false}
                width={50}
                cell={({ dataItem }: GridCellProps) =>
                  renderRowActions(
                    () => openEditDisable(dataItem),
                    () => handleToggleDisable(dataItem),
                    () => setDeleteModal({ type: 'disable', id: dataItem.id }),
                    dataItem.enabled
                  )
                }
              />
            </Grid>
          </Loading>
        </section>

        <section>
          <div className='flex items-center justify-between'>
            <Body1 bold>Amount Overrides</Body1>
            <AddButton
              text='Create Amount Override'
              disabled={!canEdit}
              onClick={openCreateAmount}
            />
          </div>
          <Loading isLoading={isLoadingAmount} fullWidth={false}>
            <Grid data={amountOverrides ?? []}>
              {amountOverridesColumns
                .filter((column) => column.show !== false)
                .map((column, index) => (
                  <Column key={index} {...column} />
                ))}
              <Column
                title=''
                filterable={false}
                width={50}
                cell={({ dataItem }: GridCellProps) =>
                  renderRowActions(
                    () => openEditAmount(dataItem),
                    () => handleToggleAmount(dataItem),
                    () => setDeleteModal({ type: 'amount', id: dataItem.id }),
                    dataItem.enabled
                  )
                }
              />
            </Grid>
          </Loading>
        </section>
      </div>

      {disableModal && (
        <DisableOverrideForm
          isOpen
          title={disableModal.title}
          initialValues={disableModal.initialValues}
          options={scopeOptions}
          onClose={() => setDisableModal(null)}
          onSubmit={handleSubmitDisable}
        />
      )}

      {amountModal && (
        <AmountOverrideForm
          isOpen
          title={amountModal.title}
          initialValues={amountModal.initialValues}
          options={scopeOptions}
          currencyOptions={currencyOptions}
          onClose={() => setAmountModal(null)}
          onSubmit={handleSubmitAmount}
        />
      )}

      {deleteModal && (
        <ConfirmationModal
          visible
          yesButtonText='Yes, delete'
          contentText='Are you sure you want to delete this override rule? This action cannot be undone.'
          onYes={handleConfirmDelete}
          onCancel={() => setDeleteModal(null)}
        />
      )}
    </>
  )
}
